import React, { useEffect, useState } from 'react'

import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import { Card } from '../../components/UI'
import {
    Gauge, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
    Clock, Zap, Activity, ChevronDown, RefreshCw, Settings2, Lightbulb
} from 'lucide-react'

// ============================================
// Core Web Vitals Budgets
// ============================================

interface Budget {
    metric: string
    label: string
    desc: string
    unit: string
    good: number
    warning: number
    icon: React.ReactNode
}

const BUDGETS: Budget[] = [
    { metric: 'lcp', label: 'LCP', desc: 'Largest Contentful Paint — Tiempo hasta que el contenido principal es visible', unit: 'ms', good: 2500, warning: 4000, icon: <Clock size={20} /> },
    { metric: 'fid', label: 'FID / INP', desc: 'First Input Delay / Interaction to Next Paint — Respuesta a primera interacción', unit: 'ms', good: 100, warning: 300, icon: <Zap size={20} /> },
    { metric: 'cls', label: 'CLS', desc: 'Cumulative Layout Shift — Estabilidad visual del layout', unit: '', good: 0.1, warning: 0.25, icon: <Activity size={20} /> },
    { metric: 'ttfb', label: 'TTFB', desc: 'Time to First Byte — Tiempo de respuesta del servidor', unit: 'ms', good: 800, warning: 1800, icon: <Gauge size={20} /> },
    { metric: 'fcp', label: 'FCP', desc: 'First Contentful Paint — Primer render de contenido', unit: 'ms', good: 1800, warning: 3000, icon: <TrendingUp size={20} /> },
]

interface PerfData {
    metric: string
    value: number
    timestamp: string
}

type TabType = 'overview' | 'details' | 'tips'

export default function PerformancePage() {
    const [licenses, setLicenses] = useState<Array<{ id: string; client_name: string; projects?: { name: string } }>>([])
    const [selectedLicense, setSelectedLicense] = useState('')
    const [perfData, setPerfData] = useState<PerfData[]>([])
    const [loading, setLoading] = useState(true)
    const [tab, setTab] = useState<TabType>('overview')

    useEffect(() => { fetchLicenses() }, [])
    useEffect(() => { if (selectedLicense) fetchPerfData() }, [selectedLicense])

    const fetchLicenses = async () => {
        try {
            const { data, error } = await supabase
                .from('licenses')
                .select('id, client_name, projects(name)')
                .order('client_name')
            if (error) throw error
            setLicenses(data || [])
            if (data?.length) setSelectedLicense(data[0].id)
        } catch {
            toast.error('Error cargando licencias')
        } finally {
            setLoading(false)
        }
    }

    const fetchPerfData = async () => {
        try {
            const { data, error } = await supabase
                .from('telemetry_events')
                .select('payload, created_at')
                .eq('license_id', selectedLicense)
                .eq('event_type', 'performance')
                .order('created_at', { ascending: false })
                .limit(100)
            if (error) throw error

            const parsed: PerfData[] = (data || []).flatMap(row => {
                const p = typeof row.payload === 'string' ? JSON.parse(row.payload) : row.payload
                return Object.entries(p || {})
                    .filter(([key]) => BUDGETS.some(b => b.metric === key))
                    .map(([key, val]) => ({
                        metric: key,
                        value: Number(val),
                        timestamp: row.created_at,
                    }))
            })
            setPerfData(parsed)
        } catch {
            // Sin datos de performance — normal si no hay telemetría
        }
    }

    const getLatestValue = (metric: string): number | null => {
        const entry = perfData.find(d => d.metric === metric)
        return entry ? entry.value : null
    }

    const getStatus = (budget: Budget, value: number | null): 'good' | 'warning' | 'poor' | 'none' => {
        if (value === null) return 'none'
        if (value <= budget.good) return 'good'
        if (value <= budget.warning) return 'warning'
        return 'poor'
    }

    const statusStyles = {
        good: { bg: 'bg-neon-green/5', border: 'border-neon-green/20', text: 'text-neon-green', label: 'Bueno' },
        warning: { bg: 'bg-yellow-500/5', border: 'border-yellow-500/20', text: 'text-yellow-400', label: 'Mejorable' },
        poor: { bg: 'bg-red-500/5', border: 'border-red-500/20', text: 'text-red-400', label: 'Pobre' },
        none: { bg: 'bg-white/[0.02]', border: 'border-white/5', text: 'text-gray-600', label: 'Sin datos' },
    }

    const overallScore = BUDGETS.reduce((acc, b) => {
        const v = getLatestValue(b.metric)
        const s = getStatus(b, v)
        return acc + (s === 'good' ? 1 : s === 'warning' ? 0.5 : 0)
    }, 0)
    const scorePercent = Math.round((overallScore / BUDGETS.length) * 100)

    const tabs: Array<{ id: TabType; label: string; icon: React.ReactNode }> = [
        { id: 'overview', label: 'Overview', icon: <Gauge size={16} /> },
        { id: 'details', label: 'Detalle Métricas', icon: <Activity size={16} /> },
        { id: 'tips', label: 'Recomendaciones', icon: <Lightbulb size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <Gauge className="text-neon-blue" /> Performance Budget
                </h1>
                <p className="text-gray-500 text-sm">Core Web Vitals + budgets de rendimiento por proyecto</p>
            </div>

            {/* Selector de Licencia */}
            <Card className="mb-6">
                <div className="flex items-center gap-4">
                    <div className="relative flex-1">
                        <select
                            value={selectedLicense}
                            onChange={e => setSelectedLicense(e.target.value)}
                            className="w-full bg-charcoal border border-white/10 rounded-lg px-4 py-3 text-white appearance-none focus:border-neon-blue focus:ring-1 focus:ring-neon-blue outline-none transition-all"
                        >
                            {licenses.map(l => (
                                <option key={l.id} value={l.id}>{l.client_name} — {l.projects?.name || 'Sin proyecto'}</option>
                            ))}
                        </select>
                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                    <button onClick={fetchPerfData} className="p-2.5 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
                        <RefreshCw size={16} />
                    </button>
                </div>
            </Card>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}
                    >
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {/* Tab: Overview */}
            {tab === 'overview' && (
                <>
                    {/* Score global */}
                    <Card className="mb-6">
                        <div className="flex items-center gap-6">
                            <div className="relative w-24 h-24 shrink-0">
                                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
                                    <circle
                                        cx="18" cy="18" r="15.5" fill="none"
                                        stroke={scorePercent >= 80 ? '#00ff88' : scorePercent >= 50 ? '#eab308' : '#ef4444'}
                                        strokeWidth="3"
                                        strokeDasharray={`${scorePercent * 0.975} 100`}
                                        strokeLinecap="round"
                                        className="transition-all duration-700"
                                    />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className={`text-xl font-bold font-mono ${scorePercent >= 80 ? 'text-neon-green' : scorePercent >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>{scorePercent}%</span>
                                </div>
                            </div>
                            <div>
                                <h2 className="text-white font-bold text-lg">Performance Score</h2>
                                <p className="text-gray-500 text-sm mt-1">
                                    {scorePercent >= 80 ? '🎉 Excelente rendimiento — todos los budgets cumplidos' :
                                        scorePercent >= 50 ? '⚠️ Rendimiento aceptable — algunas métricas necesitan mejora' :
                                            '❌ Rendimiento insuficiente — revisar métricas críticas'}
                                </p>
                            </div>
                        </div>
                    </Card>

                    {/* Grid de métricas */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {BUDGETS.map(budget => {
                            const value = getLatestValue(budget.metric)
                            const status = getStatus(budget, value)
                            const style = statusStyles[status]
                            return (
                                <Card key={budget.metric} className={`${style.bg} border ${style.border}`}>
                                    <div className="flex items-start justify-between mb-3">
                                        <div className={style.text}>{budget.icon}</div>
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold border ${style.border} ${style.bg} ${style.text}`}>
                                            {style.label}
                                        </span>
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-2xl font-bold font-mono text-white">
                                            {value !== null ? (budget.metric === 'cls' ? value.toFixed(3) : Math.round(value)) : '—'}
                                        </span>
                                        <span className="text-gray-600 text-xs">{budget.unit}</span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1 font-medium">{budget.label}</div>

                                    {/* Budget bar */}
                                    <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all duration-500 ${status === 'good' ? 'bg-neon-green' : status === 'warning' ? 'bg-yellow-500' : status === 'poor' ? 'bg-red-500' : 'bg-gray-700'}`}
                                            style={{ width: value !== null ? `${Math.min((value / budget.warning) * 100, 100)}%` : '0%' }}
                                        />
                                    </div>
                                    <div className="flex justify-between mt-1 text-[10px] text-gray-600 font-mono">
                                        <span>0</span>
                                        <span className="text-neon-green/50">bueno ≤{budget.good}{budget.unit}</span>
                                        <span className="text-red-400/50">&gt;{budget.warning}{budget.unit}</span>
                                    </div>
                                </Card>
                            )
                        })}
                    </div>
                </>
            )}

            {/* Tab: Detalle */}
            {tab === 'details' && (
                <div className="space-y-4">
                    {BUDGETS.map(budget => {
                        const metricData = perfData.filter(d => d.metric === budget.metric).slice(0, 10)
                        return (
                            <Card key={budget.metric}>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="text-neon-blue">{budget.icon}</div>
                                    <div>
                                        <h3 className="text-white font-bold text-sm">{budget.label}</h3>
                                        <p className="text-gray-500 text-xs">{budget.desc}</p>
                                    </div>
                                </div>

                                {/* Mini sparkline */}
                                <div className="flex items-end gap-1 h-12 mb-3">
                                    {metricData.length > 0 ? metricData.reverse().map((d, i) => {
                                        const status = getStatus(budget, d.value)
                                        const maxVal = budget.warning * 1.5
                                        const height = Math.max(4, Math.min((d.value / maxVal) * 48, 48))
                                        return (
                                            <div
                                                key={i}
                                                className={`flex-1 rounded-t transition-all ${status === 'good' ? 'bg-neon-green/40' : status === 'warning' ? 'bg-yellow-500/40' : 'bg-red-500/40'}`}
                                                style={{ height: `${height}px` }}
                                                title={`${d.value}${budget.unit} — ${new Date(d.timestamp).toLocaleString('es-ES')}`}
                                            />
                                        )
                                    }) : (
                                        <div className="flex-1 flex items-center justify-center text-gray-600 text-xs">Sin datos de telemetría</div>
                                    )}
                                </div>

                                {/* Thresholds */}
                                <div className="flex gap-4 text-xs">
                                    <span className="flex items-center gap-1.5">
                                        <CheckCircle2 size={12} className="text-neon-green" />
                                        <span className="text-gray-500">Bueno: ≤{budget.good}{budget.unit}</span>
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <AlertTriangle size={12} className="text-yellow-400" />
                                        <span className="text-gray-500">Mejorable: ≤{budget.warning}{budget.unit}</span>
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <TrendingDown size={12} className="text-red-400" />
                                        <span className="text-gray-500">Pobre: &gt;{budget.warning}{budget.unit}</span>
                                    </span>
                                </div>
                            </Card>
                        )
                    })}
                </div>
            )}

            {/* Tab: Tips */}
            {tab === 'tips' && (
                <div className="space-y-4">
                    {[
                        { title: 'Optimizar LCP', icon: '⚡', tips: ['Precargar fuentes y CSS crítico', 'Comprimir imágenes hero con WebP/AVIF', 'Usar <link rel="preload"> para recursos above-the-fold', 'Implementar Server-Side Rendering (SSR) si es viable'] },
                        { title: 'Reducir FID / INP', icon: '🎯', tips: ['Dividir JavaScript en chunks más pequeños (code splitting)', 'Mover cálculos pesados a Web Workers', 'Usar requestIdleCallback para tareas no críticas', 'Reducir third-party scripts al mínimo'] },
                        { title: 'Minimizar CLS', icon: '📐', tips: ['Definir width/height en todas las imágenes y videos', 'Reservar espacio para contenido dinámico (skeletons)', 'Evitar insertar contenido encima del viewport visible', 'Usar font-display: swap con fuentes web'] },
                        { title: 'Mejorar TTFB', icon: '🌐', tips: ['Usar CDN (Cloudflare) para servir assets estáticos', 'Habilitar compresión Brotli/gzip en el servidor', 'Implementar caching con Cache-Control headers', 'Reducir redirecciones HTTP innecesarias'] },
                        { title: 'Acelerar FCP', icon: '🎨', tips: ['Inlinear CSS crítico en el <head>', 'Diferir JavaScript no esencial con async/defer', 'Minimizar el HTML del documento inicial', 'Usar resource hints: dns-prefetch, preconnect'] },
                    ].map((section, i) => (
                        <Card key={i}>
                            <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                                <span className="text-lg">{section.icon}</span> {section.title}
                            </h3>
                            <ul className="space-y-2">
                                {section.tips.map((tip, j) => (
                                    <li key={j} className="flex items-start gap-2 text-sm text-gray-400">
                                        <span className="w-1.5 h-1.5 rounded-full bg-neon-blue mt-1.5 shrink-0" />
                                        {tip}
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    ))}

                    <Card className="bg-neon-blue/5 border border-neon-blue/20">
                        <div className="flex items-center gap-3">
                            <Settings2 size={20} className="text-neon-blue" />
                            <div>
                                <h3 className="text-white font-bold text-sm">Torre de Control ya optimiza</h3>
                                <p className="text-gray-400 text-xs mt-1">El agente vendor_core.ts usa PerformanceObserver, backoff exponencial, Visibility API y telemetría mínima (RGPD Art. 5.1.c) para minimizar su impacto en el rendimiento.</p>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    )
}
